import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import {
  Check, X, Minus, Plus, Shield, FileCheck, RotateCcw,
  ChevronDown, ChevronUp, Loader2, Sparkles,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { api } from '../utils/api';
import { track } from '../utils/observability';
import { toast } from 'sonner';

/**
 * UPGRADE-01 — Conversion-focused upgrade page.
 *
 * Differences vs /billing (intentional, do not refactor /billing to share):
 *   - No subscription management widgets, no credit pack purchases
 *   - One CTA per plan; Solo card visually highlighted as "Mais escolhido"
 *   - Personalized banner for Starter users with consultations_used > 0
 *   - Source-aware analytics (?source=...) for funnel attribution
 *   - Trust strip + FAQ to absorb objections without leaving the page
 */
export default function UpgradePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { isDark } = useTheme();

  const [isAnnual, setIsAnnual] = useState(false);
  const [clinicSeats, setClinicSeats] = useState(3);
  const [usage, setUsage] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [tableOpen, setTableOpen] = useState(false);

  const source = searchParams.get('source') || null;
  const currentPlan = user?.plan_type || 'starter';

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const u = await api.getUsageStats();
        if (mounted) setUsage(u);
      } catch {
        // best-effort — page still renders without the personalized banner
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Fire upgrade_page_viewed on mount (once usage is hydrated or known absent)
  useEffect(() => {
    track('upgrade_page_viewed', {
      plan_type: currentPlan,
      consultations_used: usage?.consultations_used ?? 0,
      source,
    });
    // We want one event per mount; deps intentionally empty.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCheckout = async (planType) => {
    if (planType === currentPlan && planType !== 'starter') return;
    if (planType === 'starter') {
      // The downgrade flow lives in /billing per spec — route there.
      navigate('/billing');
      return;
    }
    track('upgrade_plan_selected', {
      selected_plan: planType,
      billing_interval: isAnnual ? 'annual' : 'monthly',
      seats: planType === 'clinica' ? clinicSeats : 1,
      source,
    });
    setLoadingPlan(planType);
    try {
      const res = await api.createCheckout({
        plan_type: planType,
        billing_interval: isAnnual ? 'annual' : 'monthly',
        seats: planType === 'clinica' ? clinicSeats : 1,
        origin_url: window.location.origin,
      });
      window.location.href = res.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao criar checkout');
      setLoadingPlan(null);
    }
  };

  const consultsUsed = usage?.consultations_used ?? 0;
  const showPersonalized = currentPlan === 'starter' && consultsUsed > 0;

  // Pricing helpers
  const soloPrice = useMemo(() => isAnnual ? '103,20' : '129', [isAnnual]);
  const soloAnnualSavings = useMemo(() => Math.floor((129 - 103.20) * 12), []);
  const clinicaPerSeat = isAnnual ? 79.20 : 99;
  const clinicaTotalMonthly = clinicaPerSeat * clinicSeats;

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto px-4 py-8" data-testid="upgrade-page">
        {/* Personalized banner — Starter w/ usage */}
        {showPersonalized && (
          <div
            className={`mb-6 rounded-xl px-5 py-4 border ${
              isDark
                ? 'bg-teal-950/30 border-teal-900/60 text-teal-100'
                : 'bg-teal-50 border-teal-200 text-teal-900'
            }`}
            data-testid="upgrade-personalized-banner"
          >
            <p className="text-sm leading-snug">
              <Sparkles className="inline w-4 h-4 mr-1.5 mb-0.5" />
              Você já gerou <strong data-testid="upgrade-personalized-count">{consultsUsed}</strong>{' '}
              {consultsUsed === 1 ? 'documento' : 'documentos'} com o Akali AI.
              Imagine com <strong>80 consultas por mês</strong>.
            </p>
          </div>
        )}

        {/* Hero */}
        <div className="text-center mb-8">
          <h1
            className={`text-4xl sm:text-5xl font-bold mb-3 ${isDark ? 'text-slate-100' : 'text-slate-900'}`}
            data-testid="upgrade-title"
          >
            Desbloqueie o potencial completo do Akali AI
          </h1>
          <p className={`text-base ${isDark ? 'text-slate-400' : 'text-slate-600'} max-w-2xl mx-auto`}>
            Escolha o plano ideal para sua prática. Sem surpresas, cancele quando quiser.
          </p>
        </div>

        {/* Billing toggle */}
        <div className="flex items-center justify-center gap-3 mb-10">
          <span className={`text-sm font-medium ${!isAnnual ? (isDark ? 'text-slate-100' : 'text-slate-900') : (isDark ? 'text-slate-500' : 'text-slate-400')}`}>
            Mensal
          </span>
          <Switch
            checked={isAnnual}
            onCheckedChange={setIsAnnual}
            data-testid="upgrade-billing-toggle"
            aria-label="Alternar entre mensal e anual"
          />
          <div className="flex items-center gap-2">
            <span className={`text-sm font-medium ${isAnnual ? (isDark ? 'text-slate-100' : 'text-slate-900') : (isDark ? 'text-slate-500' : 'text-slate-400')}`}>
              Anual
            </span>
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300">
              -20%
            </span>
          </div>
        </div>

        {/* Plan cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
          <PlanCard
            testId="upgrade-card-starter"
            label="Starter"
            sub="Para começar"
            price="Grátis"
            features={[
              '15 consultas/mês',
              'Gravação e transcrição PT-BR',
              'Documentação com IA',
            ]}
            cta={
              currentPlan === 'starter' ? (
                <Button disabled className="w-full" variant="secondary" data-testid="upgrade-starter-cta">
                  Plano atual
                </Button>
              ) : (
                <Button
                  onClick={() => handleCheckout('starter')}
                  variant="outline"
                  className="w-full"
                  data-testid="upgrade-starter-cta"
                >
                  Fazer downgrade
                </Button>
              )
            }
          />

          <PlanCard
            testId="upgrade-card-solo"
            highlighted
            label="Solo"
            sub="Para profissionais autônomos"
            price={`R$ ${soloPrice}`}
            priceSuffix="/mês"
            badge="Mais escolhido"
            extraNote={
              isAnnual ? (
                <span className="block text-xs mt-1 text-emerald-600 dark:text-emerald-400">
                  Economize R$ {soloAnnualSavings}/ano
                </span>
              ) : null
            }
            features={[
              '80 consultas/mês',
              'Síntese de Raciocínio Clínico (SRC)',
              'Exportação PDF com logo da clínica',
              'Prompts personalizados',
              'Suporte por email (48h)',
            ]}
            cta={
              currentPlan === 'solo' ? (
                <Button disabled className="w-full bg-emerald-600 hover:bg-emerald-600 text-white" data-testid="upgrade-solo-cta">
                  Você já está neste plano <Check className="w-4 h-4 ml-1.5" />
                </Button>
              ) : (
                <Button
                  onClick={() => handleCheckout('solo')}
                  disabled={loadingPlan === 'solo'}
                  className="w-full bg-primary hover:bg-primary-hover"
                  data-testid="upgrade-solo-cta"
                >
                  {loadingPlan === 'solo' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Assinar Solo →
                </Button>
              )
            }
          />

          <PlanCard
            testId="upgrade-card-clinica"
            label="Clínica"
            sub="Para clínicas com equipes"
            price={`R$ ${clinicaPerSeat.toFixed(2).replace('.', ',')}`}
            priceSuffix="/assento/mês"
            extraNote={
              <span className="block text-xs mt-1 text-slate-500 dark:text-slate-400">
                Mínimo 3 assentos · R$ {(clinicaPerSeat * 3).toFixed(0)}/mês
              </span>
            }
            extra={
              <div className="my-4">
                <label className="text-xs font-medium mb-1.5 block text-slate-500 dark:text-slate-400">
                  Assentos
                </label>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setClinicSeats((s) => Math.max(3, s - 1))}
                    disabled={clinicSeats <= 3}
                    data-testid="upgrade-clinica-seats-dec"
                  >
                    <Minus className="w-3.5 h-3.5" />
                  </Button>
                  <input
                    type="number"
                    min={3}
                    max={20}
                    value={clinicSeats}
                    onChange={(e) => {
                      const v = parseInt(e.target.value || '3', 10);
                      if (!Number.isNaN(v)) setClinicSeats(Math.min(20, Math.max(3, v)));
                    }}
                    className={`flex-1 text-center border rounded-md py-1.5 text-sm font-semibold ${
                      isDark ? 'bg-slate-800 border-slate-700 text-slate-100' : 'bg-white border-slate-300 text-slate-900'
                    }`}
                    data-testid="upgrade-clinica-seats-input"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setClinicSeats((s) => Math.min(20, s + 1))}
                    disabled={clinicSeats >= 20}
                    data-testid="upgrade-clinica-seats-inc"
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <p className={`text-xs mt-2 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                  Total:{' '}
                  <span className="font-semibold" data-testid="upgrade-clinica-total">
                    R$ {clinicaTotalMonthly.toFixed(0)}/mês
                  </span>
                </p>
              </div>
            }
            features={[
              'Tudo do Solo × assentos',
              'Dashboard administrativo',
              'Relatórios consolidados da clínica',
              'Gestão de múltiplos profissionais',
              'Suporte prioritário (24h)',
            ]}
            cta={
              currentPlan === 'clinica' ? (
                <Button disabled className="w-full bg-emerald-600 hover:bg-emerald-600 text-white" data-testid="upgrade-clinica-cta">
                  Você já está neste plano <Check className="w-4 h-4 ml-1.5" />
                </Button>
              ) : (
                <Button
                  onClick={() => handleCheckout('clinica')}
                  disabled={loadingPlan === 'clinica'}
                  variant="outline"
                  className="w-full"
                  data-testid="upgrade-clinica-cta"
                >
                  {loadingPlan === 'clinica' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Assinar Clínica →
                </Button>
              )
            }
          />
        </div>

        {/* Trust strip */}
        <div
          className={`grid grid-cols-1 sm:grid-cols-3 gap-4 py-6 mb-10 rounded-xl ${
            isDark ? 'bg-slate-800/40' : 'bg-slate-50'
          }`}
          data-testid="upgrade-trust-strip"
        >
          <TrustItem Icon={Shield} text="Pagamento seguro via Stripe" />
          <TrustItem Icon={FileCheck} text="Conforme COFFITO 414/2012" />
          <TrustItem Icon={RotateCcw} text="Cancele quando quiser" />
        </div>

        {/* Comparison table — collapsible */}
        <div className="mb-10">
          <button
            type="button"
            onClick={() => setTableOpen((v) => !v)}
            className={`w-full text-center text-sm font-medium py-3 rounded-lg border transition ${
              isDark
                ? 'border-slate-700 hover:bg-slate-800/50 text-slate-200'
                : 'border-slate-200 hover:bg-slate-50 text-slate-700'
            }`}
            data-testid="upgrade-table-toggle"
          >
            Ver comparação completa{' '}
            {tableOpen ? <ChevronUp className="inline w-4 h-4 ml-1" /> : <ChevronDown className="inline w-4 h-4 ml-1" />}
          </button>
          {tableOpen && (
            <div
              className={`mt-4 rounded-xl border overflow-x-auto ${
                isDark ? 'bg-slate-800/40 border-slate-700' : 'bg-white border-slate-200'
              }`}
              data-testid="upgrade-comparison-table"
            >
              <table className="w-full text-sm">
                <thead className={isDark ? 'bg-slate-800/60' : 'bg-slate-50'}>
                  <tr className={`text-left ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                    <th className="py-3 px-4 font-medium" />
                    <th className="py-3 px-4 font-semibold text-center">Starter</th>
                    <th className="py-3 px-4 font-semibold text-center">Solo</th>
                    <th className="py-3 px-4 font-semibold text-center">Clínica</th>
                  </tr>
                </thead>
                <tbody>
                  <ComparisonRow label="Consultas/mês" starter="15" solo="80" clinica="80/assento" />
                  <ComparisonRow label="Gravação e transcrição" starter="check" solo="check" clinica="check" />
                  <ComparisonRow label="Documentação com IA" starter="check" solo="check" clinica="check" />
                  <ComparisonRow label="Síntese Raciocínio Clínico" starter="x" solo="check" clinica="check" />
                  <ComparisonRow label="Exportação PDF" starter="Básico" solo="Com logo" clinica="Com logo" />
                  <ComparisonRow label="Prompts personalizados" starter="x" solo="check" clinica="check" />
                  <ComparisonRow label="Anexos clínicos" starter="check" solo="check" clinica="check" />
                  <ComparisonRow label="Dashboard admin" starter="x" solo="x" clinica="check" />
                  <ComparisonRow label="Relatórios clínica" starter="x" solo="x" clinica="check" />
                  <ComparisonRow label="Suporte" starter="—" solo="Email 48h" clinica="Email 24h" />
                  <ComparisonRow label="Créditos adicionais" starter="x" solo="check" clinica="check" />
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* FAQ */}
        <div className="mb-10" data-testid="upgrade-faq">
          <h2 className={`text-2xl font-semibold mb-4 ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
            Perguntas frequentes
          </h2>
          <div className="space-y-4">
            <FaqItem
              q="Posso cancelar a qualquer momento?"
              a="Sim. Não há fidelidade. Você pode cancelar pelo painel de assinatura a qualquer momento e continua com acesso até o fim do período já pago."
            />
            <FaqItem
              q="O que acontece com meus dados se eu cancelar?"
              a="Seus prontuários e histórico de pacientes ficam disponíveis para consulta por 30 dias após o cancelamento. Você pode exportar tudo em PDF antes disso."
            />
            <FaqItem
              q="Posso mudar de plano depois?"
              a="Sim. Você pode fazer upgrade ou downgrade a qualquer momento pelo painel de assinatura."
            />
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center pb-12">
          <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            Ainda com dúvidas?{' '}
            <a
              href="mailto:contato@akalipilates.com"
              className="text-primary hover:opacity-80 font-medium"
              data-testid="upgrade-contact-link"
            >
              Fale com a gente → contato@akalipilates.com
            </a>
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
}

/* ============================== Sub-components ============================== */

const PlanCard = ({ testId, label, sub, price, priceSuffix, features, cta, badge, extraNote, extra, highlighted }) => {
  const { isDark } = useTheme();
  const base = isDark
    ? 'bg-slate-800/40 border-slate-700'
    : 'bg-white border-slate-200';
  const highlight = highlighted
    ? (isDark
        ? 'border-2 border-purple-500 shadow-[0_0_0_3px_rgba(168,85,247,0.15)]'
        : 'border-2 border-purple-500 shadow-[0_0_0_3px_rgba(168,85,247,0.10)]')
    : 'border';
  return (
    <div
      className={`relative rounded-2xl p-6 flex flex-col ${base} ${highlight}`}
      data-testid={testId}
    >
      {badge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span
            className="text-[11px] font-semibold px-3 py-1 rounded-full text-white"
            style={{ background: 'linear-gradient(135deg, #f97316, #a855f7)' }}
            data-testid={`${testId}-badge`}
          >
            {badge}
          </span>
        </div>
      )}
      <div className="mb-3">
        <h3 className={`text-lg font-semibold ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>{label}</h3>
        <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{sub}</p>
      </div>
      <div className="mb-4">
        <div className={`text-3xl font-bold ${isDark ? 'text-slate-100' : 'text-slate-900'}`} data-testid={`${testId}-price`}>
          {price}
          {priceSuffix && <span className={`text-sm font-medium ml-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{priceSuffix}</span>}
        </div>
        {extraNote}
      </div>
      {extra}
      <ul className="space-y-2 mb-5 flex-1">
        {features.map((f, i) => (
          <li key={i} className={`flex items-start gap-2 text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
            <Check className="w-4 h-4 mt-0.5 shrink-0 text-emerald-500" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      {cta}
    </div>
  );
};

const TrustItem = ({ Icon, text }) => {
  const { isDark } = useTheme();
  return (
    <div className="flex items-center justify-center gap-2 px-4">
      <Icon className={`w-4 h-4 shrink-0 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
      <span className={`text-xs ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{text}</span>
    </div>
  );
};

const ComparisonRow = ({ label, starter, solo, clinica }) => {
  const { isDark } = useTheme();
  const Cell = ({ v }) => {
    if (v === 'check') return <Check className="w-4 h-4 mx-auto text-emerald-500" />;
    if (v === 'x') return <X className={`w-4 h-4 mx-auto ${isDark ? 'text-slate-600' : 'text-slate-300'}`} />;
    return <span>{v}</span>;
  };
  return (
    <tr className={`border-t ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
      <td className={`py-3 px-4 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{label}</td>
      <td className={`py-3 px-4 text-center text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}><Cell v={starter} /></td>
      <td className={`py-3 px-4 text-center text-sm font-medium ${isDark ? 'text-slate-200' : 'text-slate-900'}`}><Cell v={solo} /></td>
      <td className={`py-3 px-4 text-center text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}><Cell v={clinica} /></td>
    </tr>
  );
};

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  const { isDark } = useTheme();
  return (
    <div
      className={`rounded-lg border ${isDark ? 'bg-slate-800/40 border-slate-700' : 'bg-white border-slate-200'}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`w-full text-left px-4 py-3 flex items-center justify-between gap-3 ${
          isDark ? 'text-slate-100' : 'text-slate-900'
        }`}
        data-testid="upgrade-faq-q"
      >
        <span className="text-sm font-medium">{q}</span>
        {open ? <ChevronUp className="w-4 h-4 shrink-0" /> : <ChevronDown className="w-4 h-4 shrink-0" />}
      </button>
      {open && (
        <p className={`px-4 pb-4 text-sm leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
          {a}
        </p>
      )}
    </div>
  );
};
